import type { ColorThemeSetName } from "@@/shared/stylable/color-sets/color-theme-sets";
import type {
    TranslatableValue} from "@@/shared/translations/translation";
import {
    translation,
} from "@@/shared/translations/translation";
import { toObjectMap } from "@@/utilities/dictionary";

const orderStatusTypes = [
    "CANCELLED",
    "CREATED",
    "FULFILLED",
    "REJECTED",
    "CONFIRMED",
    "READY_FOR_PICK_UP",
    "PENDING_CONFIRMATION",
    "PENDING_PAYMENT_CONFIRMATION",
    "PENDING_DELIVERY",
    "OUT_FOR_DELIVERY",
    "PAYMENT_PENDING",
    "PAYMENT_FULFILLED",
    "PAYMENT_FAILED",
    "PAYMENT_REFUND_PENDING",
    "PAYMENT_REFUND_FAILED",
    "PAYMENT_REFUND_FULFILLED",
    "PAYMENT_REFUND_INITIATED",
    "BOOKABLE_TIME_CHANGE",
    "BOOKABLE_TIME_UPDATED",
    "BOOKABLE_TABLE_RESERVATION_UPDATED",
] as const;
type OrderStatusType = (typeof orderStatusTypes)[number];
const orderStatusType = toObjectMap<OrderStatusType>([...orderStatusTypes]);

const orderStatusTypeSettings: Record<
    OrderStatusType,
    {
        title: TranslatableValue;
        state: OrderStatusType;
        preferredColorThemeSet?: ColorThemeSetName;
    }
> = {
    CANCELLED: {
        state: "CANCELLED",
        title: translation({
            sv: "Avbokad",
            en: "Cancelled",
        }),
        preferredColorThemeSet: "danger",
    },
    CONFIRMED: {
        state: "CONFIRMED",
        title: translation({
            sv: "Bekräftad",
            en: "Confirmed",
        }),
    },
    CREATED: {
        state: "CREATED",
        title: translation({
            sv: "Utkast",
            en: "Draft",
        }),
    },
    FULFILLED: {
        state: "FULFILLED",
        title: translation({
            sv: "Klar",
            en: "Fulfilled",
        }),
    },
    OUT_FOR_DELIVERY: {
        state: "OUT_FOR_DELIVERY",
        title: translation({
            sv: "Ute för leverans",
            en: "Out for delivery",
        }),
    },
    PAYMENT_FAILED: {
        state: "PAYMENT_FAILED",
        preferredColorThemeSet: "danger",
        title: translation({
            sv: "Betalning misslyckades",
            en: "Payment failed",
        }),
    },
    PAYMENT_FULFILLED: {
        state: "PAYMENT_FULFILLED",
        title: translation({
            sv: "Order har betalats",
            en: "Order payment has been fulfilled",
        }),
    },
    PAYMENT_REFUND_PENDING: {
        state: "PAYMENT_REFUND_PENDING",
        title: translation({
            sv: "Återbetalning begärd",
            en: "Refund pending",
        }),
    },
    PAYMENT_REFUND_FULFILLED: {
        state: "PAYMENT_REFUND_FULFILLED",
        title: translation({
            sv: "Återbetalning klar",
            en: "Refund fulfilled",
        }),
    },
    PAYMENT_REFUND_FAILED: {
        state: "PAYMENT_REFUND_FAILED",
        title: translation({
            sv: "Återbetalning misslyckades",
            en: "Refund failed",
        }),
    },
    PENDING_CONFIRMATION: {
        state: "PENDING_CONFIRMATION",
        title: translation({
            sv: "Väntar på bekräftelse",
            en: "Pending confirmation",
        }),
    },
    PAYMENT_PENDING: {
        state: "PAYMENT_PENDING",
        title: translation({
            sv: "Betalning väntas",
            en: "Pending payment confirmation",
        }),
    },
    PENDING_PAYMENT_CONFIRMATION: {
        state: "PENDING_PAYMENT_CONFIRMATION",
        title: translation({
            sv: "Väntar på betalningsbekräftelse",
            en: "Pending payment confirmation",
        }),
    },
    PENDING_DELIVERY: {
        state: "PENDING_DELIVERY",
        title: translation({
            sv: "Väntar på att levereras",
            en: "Pending delivery",
        }),
    },
    REJECTED: {
        state: "REJECTED",
        preferredColorThemeSet: "danger",
        title: translation({
            sv: "Nekad",
            en: "Rejected",
        }),
    },
    READY_FOR_PICK_UP: {
        state: "READY_FOR_PICK_UP",
        title: translation({
            sv: "Redo för upphämtning",
            en: "Ready for pickup",
        }),
    },
    PAYMENT_REFUND_INITIATED: {
        state: "PAYMENT_REFUND_INITIATED",
        title: translation({
            sv: "Återbetalning påbörjad",
            en: "Refund is initiated",
        }),
    },
    BOOKABLE_TIME_CHANGE: {
        state: "BOOKABLE_TIME_CHANGE",
        title: translation({
            sv: "Tid ändrad",
            en: "Time changed",
        }),
    },
    BOOKABLE_TIME_UPDATED: {
        state: "BOOKABLE_TIME_UPDATED",
        title: translation({
            sv: "Tid ändrad",
            en: "Time changed",
        }),
    },
    BOOKABLE_TABLE_RESERVATION_UPDATED: {
        state: "BOOKABLE_TABLE_RESERVATION_UPDATED",
        title: translation({
            sv: "Reservation ändrad",
            en: "Reservation changed",
        }),
    },
};

export { orderStatusType, orderStatusTypes, orderStatusTypeSettings };
export type { OrderStatusType };
